import { useState, useEffect, useRef } from 'react';

export function useWakeLock(active: boolean) {
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  const [acquired, setAcquired] = useState(false);
  const sentinelRef = useRef<WakeLockSentinel | null>(null);

  useEffect(() => {
    if (!active || !supported) {
      setAcquired(false);
      return;
    }

    let cancelled = false;

    (navigator.wakeLock as WakeLock).request('screen').then((sentinel) => {
      if (cancelled) { sentinel.release(); return; }
      sentinelRef.current = sentinel;
      setAcquired(true);
      sentinel.addEventListener('release', () => setAcquired(false));
    }).catch(() => setAcquired(false));

    return () => {
      cancelled = true;
      sentinelRef.current?.release().catch(() => {});
      sentinelRef.current = null;
    };
  }, [active, supported]);

  // Re-acquire after tab regains focus (browser releases it on hide)
  useEffect(() => {
    if (!active || !supported) return;

    const reacquire = async () => {
      if (document.visibilityState !== 'visible' || sentinelRef.current) return;
      try {
        const sentinel = await (navigator.wakeLock as WakeLock).request('screen');
        sentinelRef.current = sentinel;
        setAcquired(true);
        sentinel.addEventListener('release', () => setAcquired(false));
      } catch {
        // ignore
      }
    };

    document.addEventListener('visibilitychange', reacquire);
    return () => document.removeEventListener('visibilitychange', reacquire);
  }, [active, supported]);

  return { supported, acquired };
}
